/*
  bjaf: Country suggestion bar (sections/bjaf-country-bar.liquid).
  Asks Shopify which country fits the visitor and, when it differs from the page's country, offers that market's
  shop. "Go" posts Shopify's localization form; "Stay" and close hide it for data-dismiss-days.
  Phones: a card at the bottom. From 700px: a pop-up in the middle of the screen (focus moves to it; Escape or a
  click on the dimmed page closes it, like the cross). It waits while the cookie banner or a Klaviyo form is open.
  Events: bjaf_country_bar_shown, _switch, _stay, _close (Shopify customer events + window.dataLayer).
*/
(() => {
  const bar = document.getElementById('bjaf-country-bar');
  if (!bar || bar.dataset.ready) return;
  bar.dataset.ready = '1';

  const KEY = 'bjaf_country_bar';
  const current = bar.dataset.current;
  const days = parseInt(bar.dataset.dismissDays, 10) || 30;
  const phone = window.matchMedia('(max-width: 699px)');

  let countries = {};
  try { countries = JSON.parse(bar.querySelector('[data-bjaf-cb-countries]').textContent); } catch (e) { return; }

  const readDismissed = () => {
    try {
      const v = JSON.parse(localStorage.getItem(KEY) || 'null');
      return v && v.until > Date.now() ? v : null;
    } catch (e) { return null; }
  };
  const writeDismissed = (country) => {
    try { localStorage.setItem(KEY, JSON.stringify({ country, until: Date.now() + days * 864e5 })); } catch (e) {}
  };
  const track = (name, payload) => {
    try { window.Shopify?.analytics?.publish?.(name, payload); } catch (e) {}
    try { (window.dataLayer = window.dataLayer || []).push({ event: name, ...payload }); } catch (e) {}
  };

  if (readDismissed()) return;
  if (/bot|crawl|spider|slurp|lighthouse/i.test(navigator.userAgent)) return;

  // Wait while the cookie banner or a Klaviyo form is open; on phones also sit above the product page's sticky bar.
  const cookieBannerOpen = () => {
    // Pandectes renders its banner as a <dialog> inside the <pandectes-cmp> shadow root; older builds use .cc-window.
    const dialog = document.querySelector('pandectes-cmp')?.shadowRoot?.querySelector('dialog');
    if (dialog?.open) return true;
    const el = document.querySelector('.cc-window');
    if (!el || el.classList.contains('cc-invisible')) return false;
    const r = el.getBoundingClientRect();
    return r.height > 0 && getComputedStyle(el).display !== 'none' && getComputedStyle(el).visibility !== 'hidden';
  };
  // Klaviyo signup popups and flyouts also take the bottom of a phone screen.
  const klaviyoOpen = () => [...document.querySelectorAll('[role="dialog"][class*="kl-private"]')]
    .some((el) => el.getBoundingClientRect().height > 0 && getComputedStyle(el).visibility !== 'hidden');
  // bjaf.com's own "Looking for another store?" pop-up (snippets/bjaf-geo-market-popup) goes first.
  const geoPopupOpen = () => document.documentElement.classList.contains('bjaf-geo-market-open');
  const overlayOpen = () => cookieBannerOpen() || klaviyoOpen() || geoPopupOpen();
  const placeAboveSticky = () => {
    if (!phone.matches) { bar.style.bottom = ''; return; }
    const sticky = document.querySelector('.bp-sticky.is-on');
    bar.style.bottom = sticky ? `${sticky.offsetHeight + 12}px` : '';
  };

  const fill = (code) => {
    const c = countries[code];
    const vars = { '[currency]': c.currency, '[country]': c.name, '[domain]': location.hostname.replace(/^www\./, '') };
    bar.querySelectorAll('[data-bjaf-cb-tpl]').forEach((el) => {
      el.textContent = el.dataset.bjafCbTpl.replace(/\[(currency|country|domain)\]/g, (m) => vars[m]);
    });
    bar.querySelector('[data-bjaf-cb-place]').textContent = `${c.name} · ${c.currency}`;
    const flag = bar.querySelector('[data-bjaf-cb-flag]');
    if (c.flag) flag.src = c.flag; else flag.remove();
    bar.querySelector('[data-bjaf-cb-go]').value = code;
  };

  const show = (code) => {
    fill(code);
    document.body.prepend(bar);
    bar.hidden = false;
    bar.classList.add('is-in');
    placeAboveSticky();
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { placeAboveSticky(); ticking = false; });
    }, { passive: true });
    phone.addEventListener?.('change', placeAboveSticky);
    // Step aside whenever the cookie banner or a Klaviyo form opens later on.
    const hold = setInterval(() => bar.classList.toggle('is-held', overlayOpen()), 1000);
    track('bjaf_country_bar_shown', { suggested_country: code, current_country: current });

    // From 700px it is a pop-up: modal for screen readers, focus inside, page behind it doesn't scroll.
    const opener = document.activeElement;
    const popup = !phone.matches;
    if (popup) {
      bar.setAttribute('aria-modal', 'true');
      document.documentElement.style.overflow = 'hidden';
      bar.querySelector('[data-bjaf-cb-go]').focus({ preventScroll: true });
    }
    const onKey = (event) => { if (event.key === 'Escape') hide('close'); };
    const onBackdrop = (event) => { if (event.target === bar && !phone.matches) hide('close'); };

    const hide = (reason) => {
      clearInterval(hold);
      writeDismissed(code);
      bar.hidden = true;
      bar.removeAttribute('aria-modal');
      document.documentElement.style.overflow = '';
      document.removeEventListener('keydown', onKey);
      bar.removeEventListener('click', onBackdrop);
      if (popup && opener && opener.focus) opener.focus({ preventScroll: true });
      track(`bjaf_country_bar_${reason}`, { suggested_country: code, current_country: current });
    };
    document.addEventListener('keydown', onKey);
    bar.addEventListener('click', onBackdrop);
    bar.querySelector('[data-bjaf-cb-stay]').addEventListener('click', () => hide('stay'));
    bar.querySelector('[data-bjaf-cb-close]').addEventListener('click', () => hide('close'));
    bar.querySelector('form').addEventListener('submit', () => {
      track('bjaf_country_bar_switch', { suggested_country: code, current_country: current });
    });
  };

  const whenReady = (code) => {
    let waited = 0;
    const tick = () => {
      if (overlayOpen() && waited < 120000) {
        waited += 1000;
        setTimeout(tick, 1000);
        return;
      }
      show(code);
    };
    // The cookie banner loads late: give it a moment after page load before the first check.
    const start = () => setTimeout(tick, 1500);
    if (document.readyState === 'complete') start(); else window.addEventListener('load', start, { once: true });
  };

  const root = window.Shopify?.routes?.root || '/';
  fetch(`${root}browsing_context_suggestions.json?country[enabled]=true&country[exclude]=${encodeURIComponent(current)}`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  })
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      const country = data?.suggestions?.find((s) => s.parts?.country)?.parts.country;
      const code = country?.handle;
      if (!code || code === current || !countries[code]) return;
      if ((country.confidence ?? 1) < 0.5) return;
      whenReady(code);
    })
    .catch(() => {});
})();
